import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Database, Power } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface TipoEmbedding {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  proveedor: 'cohere' | 'openai' | 'other';
  dimensiones: number;
  max_tokens: number;
  costo_token: number;
  estado: 'activo' | 'inactivo';
  created_at: string;
}

export default function TiposEmbeddings() {
  const [tipos, setTipos] = useState<TipoEmbedding[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState<TipoEmbedding | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newTipo, setNewTipo] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    proveedor: 'openai' as const,
    dimensiones: 1536,
    max_tokens: 8191,
    costo_token: 0.00002,
    estado: 'activo' as const
  });

  useEffect(() => {
    fetchTipos();
  }, []);

  const fetchTipos = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('tipos_embeddings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTipos(data || []);
    } catch (error: any) {
      console.error('Error fetching embedding types:', error);
      setError('Error al cargar los tipos de embeddings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingTipo) {
        const { error } = await supabase
          .from('tipos_embeddings')
          .update({
            codigo: newTipo.codigo,
            nombre: newTipo.nombre,
            descripcion: newTipo.descripcion || null,
            proveedor: newTipo.proveedor,
            dimensiones: newTipo.dimensiones,
            max_tokens: newTipo.max_tokens,
            costo_token: newTipo.costo_token,
            estado: newTipo.estado
          })
          .eq('id', editingTipo.id);

        if (error) throw error;
        setSuccess('Tipo de embedding actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('tipos_embeddings')
          .insert([newTipo]);

        if (error) throw error;
        setSuccess('Tipo de embedding creado correctamente');
      }

      setShowModal(false);
      setEditingTipo(null);
      setNewTipo({
        codigo: '',
        nombre: '',
        descripcion: '',
        proveedor: 'openai',
        dimensiones: 1536,
        max_tokens: 8191,
        costo_token: 0.00002,
        estado: 'activo'
      });
      fetchTipos();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (tipo: TipoEmbedding) => {
    setEditingTipo(tipo);
    setNewTipo({
      codigo: tipo.codigo,
      nombre: tipo.nombre,
      descripcion: tipo.descripcion || '',
      proveedor: tipo.proveedor,
      dimensiones: tipo.dimensiones,
      max_tokens: tipo.max_tokens,
      costo_token: tipo.costo_token,
      estado: tipo.estado
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este tipo de embedding?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('tipos_embeddings')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Tipo de embedding eliminado correctamente');
      fetchTipos();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleToggleStatus = async (tipo: TipoEmbedding) => {
    try {
      const newStatus = tipo.estado === 'activo' ? 'inactivo' : 'activo';
      const { error } = await supabase
        .from('tipos_embeddings')
        .update({ estado: newStatus })
        .eq('id', tipo.id);

      if (error) throw error;
      setSuccess(`Tipo de embedding ${newStatus === 'activo' ? 'activado' : 'desactivado'} correctamente`);
      fetchTipos();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const filteredTipos = tipos.filter(tipo =>
    tipo.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tipo.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tipo.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tipo.proveedor.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Tipos de Embeddings</h1>
        <button 
          onClick={() => {
            setEditingTipo(null);
            setNewTipo({
              codigo: '',
              nombre: '',
              descripcion: '',
              proveedor: 'openai',
              dimensiones: 1536,
              max_tokens: 8191,
              costo_token: 0.00002,
              estado: 'activo'
            });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Tipo
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar tipos de embeddings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold text-gray-600">Código</th>
                  <th className="pb-3 font-semibold text-gray-600">Nombre</th>
                  <th className="pb-3 font-semibold text-gray-600">Proveedor</th>
                  <th className="pb-3 font-semibold text-gray-600">Dimensiones</th>
                  <th className="pb-3 font-semibold text-gray-600">Max Tokens</th>
                  <th className="pb-3 font-semibold text-gray-600">Costo/Token</th>
                  <th className="pb-3 font-semibold text-gray-600">Estado</th>
                  <th className="pb-3 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredTipos.map((tipo) => (
                  <tr key={tipo.id} className="border-b">
                    <td className="py-3">
                      <div className="flex items-center">
                        <Database className="w-5 h-5 text-blue-500 mr-2" />
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {tipo.codigo}
                        </code>
                      </div>
                    </td>
                    <td className="py-3">
                      <div>
                        <div className="font-medium">{tipo.nombre}</div>
                        {tipo.descripcion && (
                          <div className="text-sm text-gray-500">{tipo.descripcion}</div>
                        )}
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        tipo.proveedor === 'openai'
                          ? 'bg-green-100 text-green-800'
                          : tipo.proveedor === 'cohere'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {tipo.proveedor}
                      </span>
                    </td>
                    <td className="py-3">
                      <code className="text-sm">{tipo.dimensiones}</code>
                    </td>
                    <td className="py-3">
                      <code className="text-sm">{tipo.max_tokens}</code>
                    </td>
                    <td className="py-3">
                      <code className="text-sm">${tipo.costo_token.toFixed(5)}</code>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        tipo.estado === 'activo'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {tipo.estado}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleToggleStatus(tipo)}
                          className={`p-1 rounded-full ${
                            tipo.estado === 'activo'
                              ? 'text-green-500 hover:bg-green-100'
                              : 'text-red-500 hover:bg-red-100'
                          }`}
                          title={tipo.estado === 'activo' ? 'Desactivar' : 'Activar'}
                        >
                          <Power className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(tipo)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Editar tipo"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(tipo.id)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                          title="Eliminar tipo"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredTipos.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron tipos de embeddings</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir/editar tipo */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] flex flex-col">
            <h2 className="text-xl font-bold mb-4">
              {editingTipo ? 'Editar Tipo de Embedding' : 'Nuevo Tipo de Embedding'}
            </h2>
            <div className="flex-1 overflow-y-auto pr-2">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código
                  </label>
                  <input
                    type="text"
                    required
                    value={newTipo.codigo}
                    onChange={(e) => setNewTipo({...newTipo, codigo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="text-embedding-3-small"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    required
                    value={newTipo.nombre}
                    onChange={(e) => setNewTipo({...newTipo, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="OpenAI Text Embedding 3 Small"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={newTipo.descripcion}
                    onChange={(e) => setNewTipo({...newTipo, descripcion: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={3}
                    placeholder="Descripción del modelo de embedding..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proveedor
                  </label>
                  <select
                    value={newTipo.proveedor}
                    onChange={(e) => setNewTipo({...newTipo, proveedor: e.target.value as 'openai' | 'cohere' | 'other'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="openai">OpenAI</option>
                    <option value="cohere">Cohere</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dimensiones
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={newTipo.dimensiones}
                    onChange={(e) => setNewTipo({...newTipo, dimensiones: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Máximo de Tokens
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={newTipo.max_tokens}
                    onChange={(e) => setNewTipo({...newTipo, max_tokens: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Costo por Token
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.00001"
                    value={newTipo.costo_token}
                    onChange={(e) => setNewTipo({...newTipo, costo_token: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    value={newTipo.estado}
                    onChange={(e) => setNewTipo({...newTipo, estado: e.target.value as 'activo' | 'inactivo'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </form>
            </div>
            <div className="flex justify-end space-x-2 mt-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingTipo(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                {editingTipo ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}